import { Button, Card, Flex, Typography } from 'antd';
import React, { FunctionComponent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Scenes from '../../scenes/scenes';
import { isEmpty } from 'lodash';
import { baseSubtitles } from '@monorepo/types';
import VideoVoiceover from '../../voiceover/voiceover';
import VideoMusic from '../../music/music';
import { useVideoContext } from '../../../../hooks/use-video';
import { BACK_BY_SCENE, VideoPart } from '../../video-editor-types';

import './video-content-container.scss';
import { CaretLeftOutlined } from '@ant-design/icons';
import ReplayScene from '../../scenes/replay-scene/replay-scene';
import VideoMetadata from '../../metadata/video-metadata';
import VideoSubtitles from '../../subtitles/video-subtitles';
import Jobs from '../../jobs/jobs';
import VideoThumbnail from '../../video-thumbnail/video-thumbnail';
import SceneItemEdit from '../../scenes/scene-items/scene-item-edit';
import Captions from '../../captions/captions';
import GlobalScene from '../../global-scene/global-scene';

const VideoContentContainer: FunctionComponent = () => {
  const { t } = useTranslation();
  const {
    video,
    currentVideoPart,
    selectedScene,
    globalScene,
    setCurrentVideoPart,
    updateSubtitles,
  } = useVideoContext();

  const TITLE_BY_PART: Record<VideoPart, string> = {
    [VideoPart.Global]: t('videoEditor.contentContainer.titles.global'),
    [VideoPart.Thumbnail]: t('videoEditor.contentContainer.titles.thumbnail'),
    [VideoPart.Scenes]: t('videoEditor.contentContainer.titles.scenes'),
    [VideoPart.Subtitles]: t('videoEditor.contentContainer.titles.subtitles'),
    [VideoPart.Voiceover]: t('videoEditor.contentContainer.titles.voiceover'),
    [VideoPart.Captions]: t('videoEditor.contentContainer.titles.captions'),
    [VideoPart.Music]: t('videoEditor.contentContainer.titles.music'),
    [VideoPart.Metadata]: t('videoEditor.contentContainer.titles.metadata'),
    [VideoPart.Jobs]: t('videoEditor.contentContainer.titles.jobs'),
    [VideoPart.EditScene]: t('videoEditor.contentContainer.titles.editScene'),
    [VideoPart.EditSceneItem]: t(
      'videoEditor.contentContainer.titles.editScene'
    ),
  };

  useEffect(() => {
    if (currentVideoPart === VideoPart.Scenes) {
      return;
    }

    const container = document.querySelector('.video-container');
    if (!container) return;

    container.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [currentVideoPart]);

  if (!video) {
    return null;
  }

  const backPart = BACK_BY_SCENE[currentVideoPart];

  const getComponentByPart = () => {
    if (currentVideoPart === VideoPart.Scenes) {
      return <Scenes />;
    }

    if (currentVideoPart === VideoPart.EditSceneItem) {
      return <SceneItemEdit />;
    }

    if (currentVideoPart === VideoPart.Subtitles) {
      return (
        <VideoSubtitles
          updateSubtitles={updateSubtitles}
          subtitlesConfig={
            isEmpty(video.subtitlesConfig)
              ? baseSubtitles
              : video.subtitlesConfig
          }
        />
      );
    }

    if (currentVideoPart === VideoPart.Captions) {
      return <Captions />;
    }

    if (currentVideoPart === VideoPart.Global) {
      return <GlobalScene />;
    }

    if (currentVideoPart === VideoPart.Voiceover) {
      return <VideoVoiceover voiceoverId={video.voiceoverId} />;
    }

    if (currentVideoPart === VideoPart.Music) {
      return (
        <VideoMusic
          backgroundMusicUrl={video.backgroundMusicUrl}
          backgroundMusicVolume={video.backgroundMusicVolume || 0.3}
          musicId={video.backgroundMusicId}
        />
      );
    }

    if (currentVideoPart === VideoPart.Metadata) {
      return <VideoMetadata />;
    }

    if (currentVideoPart === VideoPart.Jobs) {
      return <Jobs />;
    }

    if (currentVideoPart === VideoPart.Thumbnail) {
      return <VideoThumbnail />;
    }

    return 'Not Implemented';
  };

  const getExtraTitle = () => {
    if (currentVideoPart === VideoPart.EditScene) {
      if (!selectedScene) {
        return null;
      }

      return ` #${selectedScene.index + 1}`;
    }
    if (currentVideoPart === VideoPart.EditSceneItem) {
      if (!selectedScene) {
        return null;
      }
      if (globalScene?._id === selectedScene._id) {
        return null;
      }

      return ` #${selectedScene.index + 1}`;
    }
    return null;
  };

  const shouldUseCardAsBody = () => {
    if (currentVideoPart === VideoPart.Scenes) {
      return false;
    }
    if (currentVideoPart === VideoPart.Global) {
      return false;
    }

    return true;
  };

  const goBack = () => {
    // If selected scene is global
    if (currentVideoPart === VideoPart.EditSceneItem) {
      if (selectedScene?._id === globalScene?._id) {
        setCurrentVideoPart(VideoPart.Global);
        return;
      }
    }

    setCurrentVideoPart(backPart);
  };

  const TIMELINE_HEIGHT = 247;
  const HEADER_HEIGHT = 64;
  const availableHeight = window.innerHeight - TIMELINE_HEIGHT - HEADER_HEIGHT;

  return (
    <Card
      classNames={{
        body: 'scroller video-container',
      }}
      styles={{
        body: {
          height: availableHeight,
        },
      }}
      className={`video-content-container ${currentVideoPart}`}
      style={{ minHeight: '100vh', borderRadius: 0 }}
    >
      <Flex justify={'space-between'} align={'center'}>
        <Flex
          justify={'start'}
          align={'start'}
          style={{ margin: '10px 0', flexDirection: 'column' }}
        >
          <Typography.Title
            style={{ marginTop: 0 }}
            className="content-title"
            //@ts-expect-error zain ba ain
            strong={true}
            level={5}
          >
            {TITLE_BY_PART[currentVideoPart]}
            {getExtraTitle()}
          </Typography.Title>
          {backPart ? (
            <Button
              style={{ fontSize: '12px', paddingLeft: 0 }}
              size={'small'}
              onClick={goBack}
              type={'text'}
              icon={<CaretLeftOutlined />}
            >
              {t('videoEditor.contentContainer.back')}
            </Button>
          ) : null}
          {/*<Typography.Text type='secondary'>Description</Typography.Text>*/}
        </Flex>
        {backPart ? <ReplayScene /> : null}
      </Flex>
      {/*<Divider style={{ margin: '10px 0' }} />*/}
      {shouldUseCardAsBody() ? (
        <Card style={{ paddingTop: '15px' }}>{getComponentByPart()}</Card>
      ) : (
        <div>{getComponentByPart()}</div>
      )}
    </Card>
  );
};

export default VideoContentContainer;
